import { SimulationConfig } from '@/types';
import { BALL_VERTEX_SHADER, BALL_FRAGMENT_SHADER } from './shaders';
import { WebGLContext, MAX_BALLS } from './WebGLContext';
import { WebGLUtils } from './WebGLUtils';

export class TrailRenderer {
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private posBuffer: WebGLBuffer;
  private radiusBuffer: WebGLBuffer;
  private colorBuffer: WebGLBuffer;
  private alphaBuffer: WebGLBuffer;
  private posData: Float32Array;
  private radiusData: Float32Array;
  private colorData: Float32Array;
  private alphaData: Float32Array;

  constructor(private context: WebGLContext) {
    const gl = context.gl;
    this.program = WebGLUtils.createProgram(gl, BALL_VERTEX_SHADER, BALL_FRAGMENT_SHADER);
    this.posData = new Float32Array(MAX_BALLS * 2);
    this.radiusData = new Float32Array(MAX_BALLS);
    this.colorData = new Float32Array(MAX_BALLS * 3);
    this.alphaData = new Float32Array(MAX_BALLS);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    gl.bindBuffer(gl.ARRAY_BUFFER, context.geoBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.posBuffer = context.createAttribBuffer(1, 2, this.posData.byteLength);
    this.radiusBuffer = context.createAttribBuffer(2, 1, this.radiusData.byteLength);
    this.colorBuffer = context.createAttribBuffer(3, 3, this.colorData.byteLength);
    this.alphaBuffer = context.createAttribBuffer(6, 1, this.alphaData.byteLength);

    this.context.bindVertexArray(null);
  }

  render(trailCircles: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
      if (trailCircles.length === 0) return;
      const gl = this.context.gl;
      this.context.useProgram(this.program);
      this.context.bindVertexArray(this.vao);

      WebGLUtils.updateCommonUniforms(gl, this.program, this.context.width, this.context.height, camera, zoomLevel);
      this.updateUniforms(config);

      const count = Math.min(trailCircles.length, MAX_BALLS);
      const startIdx = Math.max(0, trailCircles.length - MAX_BALLS);
      for (let i = 0; i < count; i++) {
          const tc = trailCircles[startIdx + i];
          if (!tc) continue;
          
          const pr = trailCircles.length > 1 ? (startIdx + i) / (trailCircles.length - 1) : 1;
          const scale = config.trailMatchBallSize ? 1.0 : (config.trailEndScale + (config.trailStartScale - config.trailEndScale) * pr);
          const finalRadius = tc.radius * scale * (config.trailWidthMultiplier || 1.0);

          this.posData[i * 2] = tc.pos.x;
          this.posData[i * 2 + 1] = tc.pos.y;
          this.radiusData[i] = finalRadius;
          if (config.useTrailRGB !== false) {
              WebGLUtils.parseHsl(tc.hue, this.colorData, i * 3);
          } else {
              WebGLUtils.parseColor(config.trailCustomColor || '#ffffff', this.colorData, i * 3);
          }
          
          // Implement alpha fade and apply trail opacity
          const alphaFade = config.trailPersistence ? 1.0 : pr;
          this.alphaData[i] = (tc.alpha !== undefined ? tc.alpha : 1.0) * alphaFade * (config.trailOpacity || 1.0);
      }

      this.uploadData(count);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
      this.context.bindVertexArray(null);
  }

  private updateUniforms(config: SimulationConfig) {
    const gl = this.context.gl;
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_pulseSpeed'), 0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_squashFactor'), 0);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_textureType'), 0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_textureOpacity'), 0);

    // --- DIAGNOSTIC LOGGING ---
    // Uncomment for production debugging if borders persist in appearing/disappearing
    // console.log(`[TrailRenderer] persistence:${config.trailPersistence}, applyBorder:${config.trailApplyBorderToPersistence}, strokeWidth:${config.trailStrokeWidth}`);

    // LOGIC FIX: Border should apply if trailApplyBorderToPersistence is true, 
    // or if persistence is off (regular trail).
    // The previous logic erroneously forced borderWidth to 0 if persistence was ON 
    // but not explicitly set to have a border.
    const showBorder = config.trailApplyBorderToPersistence || !config.trailPersistence;
    gl.uniform1f(gl.getUniformLocation(this.program, 'i_borderWidth'), showBorder ? (config.trailStrokeWidth || 0) : 0);
    
    const tCol = WebGLUtils.hexToRgb(config.trailStrokeColor || '#000000');
    gl.uniform3f(gl.getUniformLocation(this.program, 'u_borderColor'), tCol[0]/255, tCol[1]/255, tCol[2]/255);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_strokeOpacity'), config.trailStrokeOpacity !== undefined ? config.trailStrokeOpacity : 1.0);

    const styleMap: Record<string, number> = { 'SHINY': 0, 'NEON': 1, 'CHROME': 2, 'MATTE': 3, 'GHOST': 4 };
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_ballStyle'), styleMap[config.ballStyle] || 0);

    gl.uniform1f(gl.getUniformLocation(this.program, 'u_glowAmount'), config.trailGlowAmount || 0);
    
    // Use trail custom color or white for glow (white works best for additive-like feel)
    const gCol = WebGLUtils.hexToRgb(config.trailCustomColor || '#ffffff');
    gl.uniform3f(gl.getUniformLocation(this.program, 'u_glowColor'), gCol[0]/255, gCol[1]/255, gCol[2]/255);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_glowOpacity'), 0.5);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_fillOpacity'), 1.0);
  }

  private uploadData(count: number) {
    const gl = this.context.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.posData.subarray(0, count * 2));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.radiusBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.radiusData.subarray(0, count));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.colorData.subarray(0, count * 3));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.alphaData.subarray(0, count));
  }
}
